import JSONModel from "sap/ui/model/json/JSONModel";
import Subtask from "./Subtask";
import BaseController from "sap/mcsuite/controller/BaseController";
import TreeTable from "sap/ui/table/TreeTable";
import Component from "sap/mcsuite/Component";
import PHPBackendRequest from "../handlers/PHPBackendRequest";
import VBox from "sap/m/VBox";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";

type TreeCreationParams = {
	// Backend source for data
	module: string;
	task: string;
};

type BackendTreeResponse = {
    treeview?: {
		title: string;
		group?: string;
		level?: [unknown, string];
		nav: {
			module: string;
			task: string;
			field: string;
		};
		columns: Record<
			string,
			BackendTreeColumnResponse
		>;
		data: Record<string, unknown>[];
	};
};

type TreeSubtaskModel = {
	id: string,
	minRowCount: number,
	columns: TreeColumn[],
	module: string,
	task: string,
	field: string
}

export type TreeColumn = {
	name: string;
	type: string;
	width: string;
	isComplex: boolean;
	label: string;
	pos: number | undefined;
};

export type BackendTreeColumnResponse = {
	name: string;
	type: string;
	width: string;
	pos?: number;
};


export default class Tree extends Subtask{
    private params: TreeCreationParams;
    private subtaskModel: JSONModel;
    private rowModel: JSONModel;
    private treeFragmentPath = 'sap.mcsuite.webapp.view.fragments.subtasks.Tree';
	protected eventHandlers = {
		// export: this.attachExportEvent.bind(this),
		// copy: this.attachCopyEvent.bind(this)
	};

	private static COMPLEX_TYPES: Record<string, string> = {
		client: "crm_clients_bs",
		contact: "crm_contacts_bs",
		shop: "crm_shops_bs",
		alert: "cfr_alert_bs",
		roster: "cfr_rosters_bs",
	};
	private complexTypeListBindings: Record<string, ODataListBinding> = {};


    constructor(controller: BaseController, subtaskId: string, params: TreeCreationParams) {
        super(controller, subtaskId);
        this.params = params;
		this.metadata =  {
			name: "Tree",
			subtaskId: subtaskId
		};
		this.loadTree().then((tree) => {
			tree.setBusy(true);
			const control = this.getSubtaskControl() as VBox;
			control.addItem(tree);
		}).catch((error) => {
			console.error("Error loading tree:", error);
		});
	}

// 

	private async loadTree(): Promise<TreeTable> {
		const treeData = await this.fetchTreeData();
		this.loadSubtaskModel(treeData);
		this.loadRowModel(treeData);
		const tree = await this.loadTreeFragment();
		tree.setModel(this.subtaskModel, "subtask");
		return tree;
	}

	private async loadTreeFragment(): Promise<TreeTable> {
		return this.controller.loadFragment(
			{
				name: this.treeFragmentPath,
			}
		) as Promise<TreeTable>;
	}

	private loadSubtaskModel(data: BackendTreeResponse): void {
		const treeSubtaskData: TreeSubtaskModel = {
			id: this.subtaskId,
			minRowCount: 10000,
			columns: this.getColumnData(data.treeview.columns),
			module: data.treeview.nav.module,
			task: data.treeview.nav.task,
			field: data.treeview.nav.field
		}
		this.subtaskModel = new JSONModel(treeSubtaskData);
	}

	private isComplexType(column: { type: string }): boolean {
		return column.type in Tree.COMPLEX_TYPES;
	}

	private getColumnData(columns: Record<string, BackendTreeColumnResponse>): TreeColumn[] {
		const processedColumns = [];
		for (const key in columns) {
			const col = columns[key];
			processedColumns.push({
				name: key,
				type: col.type,
				width: col.width,
				isComplex: this.isComplexType(col),
				label: col.name,
				pos: col.pos
			});
		}
		return processedColumns;
	}

	private async fetchTreeData () {
		const component: Component = this.controller.getOwnerComponent();
		const user = component.getUser();
		const request = new PHPBackendRequest(
			this.params.module,
			this.params.task,
			user,
		);

		// Support different HTTPRequest APIs (send/execute) and response shapes
        let json: BackendTreeResponse;
        try {
            const response = await request.send();
            json = await response.json() as BackendTreeResponse;
        } catch (e) {

            throw new Error(`Data fetch failed: ${(e as Error).message}`);
        }
		return json;
	} 


	public getTree(): TreeTable {
		return this.getUIElementByNameInSubtask("tree") as TreeTable;
	}

	private async loadRowModel(data: BackendTreeResponse): Promise<void> {
		const rowData = data.treeview.data;
		const columns = this.subtaskModel.getProperty("/columns") as TreeColumn[];
		const groupName = data.treeview.group || 'parent';
		let rows = await this.explodeComplexTypes(rowData, columns);
		rows = this.buildRowHierarchy(rowData, groupName);
		rows = this.groupHighestLevel(rows, data.treeview.level[1]);
	}

	private async explodeComplexTypes(rows: Record<string, unknown>[], columns: TreeColumn[]): Promise<Record<string, unknown>[]> {
		// First collect all IDs of complex types in the rows
		const rowsWithCollectedIds = this.collectComplexTypeIds(rows, columns);
		// Then fetch all complex type objects for the collected IDs in batches
		const rowsWithFetchedObjects = await this.fetchComplexTypeObjects(rowsWithCollectedIds, columns);
		this.buildComplexObjectMap();
		// Finally replace IDs with the fetched complex type objects
		const explodedRows = this.replaceIdsWithObjects(rowsWithFetchedObjects, columns);
		return explodedRows;
	}


	private collectComplexTypeIds(rows: Record<string, unknown>[], columns: TreeColumn[]): Record<string, unknown>[] {
		rows.forEach(row => {
			for (const key in row) {
				this.collectIdForColumn(row, key, columns);
			}
		});
		return rows;
	}

	private collectIdForColumn(row: Record<string, unknown>, columnKey: string, columns: TreeColumn[]): void {
		const column = columns.find(col => col.name === columnKey);
		if (!column) return;
		if (!this.isComplexType(column)) return;
		const type = column.type;
		if (!Tree.COMPLEX_TYPES[type].listbinding) return;
		const table = Tree.COMPLEX_TYPES[type];
		if (complexId) {
			this.complexTypeIds.add(complexId);
		}
	}

	private buildRowHierarchy(rows: Record<string, unknown>[], groupName: string): Record<string, unknown>[] {
		const nodes = new Map<number, Record<string, unknown>>(rows.map(item => [item.id as number, item]));
		const rootNodes: Record<string, unknown>[] = [];
		rows.forEach(item => {
			if (item[groupName] === '0' || !nodes.has(item[groupName] as number)) {
				rootNodes.push(item);
				return;
			}
			const parent = nodes.get(item[groupName] as number);
			if (!parent) {
				return;
			}
			if (!parent.children) {
				parent.children = [];
			}
			(parent.children as Record<string, unknown>[]).push(item);
		});
		return rootNodes;
	}
}